import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ANNOUNCEMENT_KIND_LABELS, type AnnouncementDTO, type NotificationDTO } from '@bifeai/shared';
import { Button, Count, EmptyState, Popover } from '@bifeai/ui';
import { Bell, BellOff, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { formatDateTime, timeAgo } from '../lib/format';

const KIND_TONE: Record<AnnouncementDTO['kind'], string> = { UPDATE: 'info', SERVICE: 'warning', NEWS: 'success', PROMO: 'promo' };

export function NotificationsBell({ unread, announcements = [] }: { unread: number; announcements?: AnnouncementDTO[] }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const news = announcements.slice(0, 5);
  const newsUnread = announcements.filter((a) => !a.read).length;
  // Al abrir la campanita, los anuncios de BifeAI se dan por vistos.
  useEffect(() => {
    if (!open || !newsUnread) return;
    const t = setTimeout(() => {
      void api
        .post('/announcements/read-all')
        .then(() => qc.invalidateQueries({ queryKey: ['announcements'] }))
        .catch(() => undefined);
    }, 1500);
    return () => clearTimeout(t);
  }, [open, newsUnread, qc]);
  const total = unread + newsUnread;
  const list = useQuery({
    queryKey: ['notifications', 'list', 8],
    queryFn: () => api.get<NotificationDTO[]>('/notifications?limit=8'),
    enabled: open,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['notifications'] });
  const readOne = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: refresh,
  });

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button type="button" className="bf-icon-btn" aria-label={total ? `Alertas: ${total} sin leer` : 'Alertas'}>
          <Bell />
          <Count value={total} tone="info" />
        </button>
      }
    >
      <div className="bf-notif-head">
        <strong style={{ fontFamily: 'var(--bf-font-display)' }}>Alertas</strong>
        {unread > 0 && (
          <Button size="sm" variant="ghost" onClick={() => readAll.mutate()} loading={readAll.isPending}>
            Marcar todas como leídas
          </Button>
        )}
      </div>
      <div className="bf-notif-list">
        {news.map((a) => (
          <div key={a.id} className="bf-news" data-read={a.read}>
            <span className={`bf-news-kind bf-news-kind--${KIND_TONE[a.kind]}`}>{ANNOUNCEMENT_KIND_LABELS[a.kind]} · BifeAI</span>
            <p className="bf-notif-title">{a.title}</p>
            {a.imageUrl && <img src={a.imageUrl} alt="" className="bf-news-img" loading="lazy" />}
            <p className="bf-notif-body" style={{ whiteSpace: 'pre-line' }}>
              {a.body}
            </p>
            {a.kind === 'SERVICE' && a.serviceStart && a.serviceEnd && (
              <p className="bf-notif-body">
                <strong>
                  {formatDateTime(a.serviceStart)} a {formatDateTime(a.serviceEnd)}
                </strong>
              </p>
            )}
            {(a.priceText || a.ctaUrl) && (
              <div className="bf-news-foot">
                {a.priceText && <strong>{a.priceText}</strong>}
                {a.ctaUrl &&
                  (a.ctaUrl.startsWith('/') ? (
                    <Link className="bf-btn bf-btn--primary bf-btn--sm" to={a.ctaUrl} onClick={() => setOpen(false)}>
                      {a.ctaLabel ?? 'Ver más'}
                    </Link>
                  ) : (
                    <a className="bf-btn bf-btn--primary bf-btn--sm" href={a.ctaUrl} target="_blank" rel="noreferrer noopener">
                      {a.ctaLabel ?? 'Ver más'} <ExternalLink size={14} aria-hidden />
                    </a>
                  ))}
              </div>
            )}
          </div>
        ))}
        {list.data?.length === 0 && !news.length && (
          <EmptyState icon={<BellOff />} title="Sin alertas" text="Acá vas a ver vencimientos, stock bajo y pagos por vencer." />
        )}
        {list.data?.map((n) => (
          <div
            key={n.id}
            className="bf-notif"
            data-read={n.read}
            role="button"
            tabIndex={0}
            onClick={() => !n.read && readOne.mutate(n.id)}
            onKeyDown={(e) => e.key === 'Enter' && !n.read && readOne.mutate(n.id)}
          >
            <span className={`bf-notif-dot bf-notif-dot--${n.severity}`} aria-hidden />
            <div>
              <p className="bf-notif-title">{n.title}</p>
              {n.body && <p className="bf-notif-body">{n.body}</p>}
              <p className="bf-notif-time">{timeAgo(n.createdAt)}</p>
            </div>
          </div>
        ))}
      </div>
      <div
        className="bf-notif-head"
        style={{
          borderBottom: 'none',
          borderTop: '1px solid var(--bf-line)',
          justifyContent: 'center',
        }}
      >
        <Link to="/alertas" onClick={() => setOpen(false)}>
          Ver todas las alertas
        </Link>
      </div>
    </Popover>
  );
}
